import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router';

import { useAppData, useIntl } from '../providers';
import { OrderData, Product } from '../interfaces';
import { CheckBox, Input, RadioGroup, Select, TextArea } from './Form';
import { Button } from './Buttons';
import { formatPrice, scrollToRef } from '../helpers';
import { OrderAsAlways } from './index';
import gtagReportOrder from '../helpers/gtagReportOrder';
import { Address, BillingAddress } from './FormSections';
import { formatFileSize } from '../helpers/format';

enum ConfirmationFieldOrder {
    productName,
    productId,
    containerAmount,
    finalPrice,

    orderAsAlways,
    containerType,
    containerAccessories,
    supplyType,
    message,
    customerType,
    name,
    phone,
    vatNumber,
    company,
    street,
    city,
    country,

    billingAddressType,
    billingCompany,
    billingStreet,
    billingCity,
    billingCountry
}

const useStyles = createUseStyles({
    form: {
        '& h3': {
            margin: '3rem 0 2rem',
        },
    },
    submitButtonWrap: {
        marginTop: '2rem',
    },
    orderButton: {
        paddingBottom: '1rem',
        paddingTop: '1rem',
        '&[disabled]': {
            cursor: 'wait',
            color: '#aaa',
            borderColor: '#aaa',
            backgroundColor: '#ddd',
        },
    },
    errorInfo: {
        marginBottom: '1rem',
        textAlign: 'center',
        color: 'red',
    },
    submittedLabel: {
        textAlign: 'center',
        color: 'var(--primary-color)',
        marginBottom: '1rem',
    },
    submittedFormData: {
        width: '100%',
        maxWidth: '700px',
        margin: '0 auto',
    },
    submissionError: {
        color: 'red',
        margin: '1rem 0',
        textAlign: 'center',
    },
    supplyTypeDescription: {
        fontSize: '90%',
        margin: '1rem 0',
    },
    accessoryList: {
        margin: '1rem 0',
        '& > * + *': {
            marginTop: '.5rem !important',
        },
    },
    orderInfo: {
        fontSize: '20px',
        color: '#000',
        fontWeight: 'bold',
    },
});

interface ProductOrderFormProps {
    product?: Product;
    handleReturn?: () => void;
    explicitOrderMode?: boolean;
}

enum CustomerType {
    new = 'newCustomer',
    existing = 'existingCustomer',
}

enum BillingAddressType {
    delivery = 'deliveryAddress',
    billing = 'billingAddress',
}

const ProductOrderForm: React.FC<ProductOrderFormProps> = ({
    product = null,
    handleReturn,
    explicitOrderMode = null,
}: ProductOrderFormProps) => {
    const classes = useStyles();
    const { translate } = useIntl();
    const {
        submitOrder,
        isSubmitting,
        termsAndConditionsUri,
        privacyUri,
        supplyTypes,
        gtagEventTarget,
        locale,
        isShop,
        allowUploads,
        maxFileSize,
    } = useAppData();
    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
        setValue: setFormValue,
    } = useForm();
    const [orderSubmitted, setOrderSubmitted] = useState(false);
    const [orderFailed, setOrderFailed] = useState(false);
    const [orderFeedback, setOrderFeedback] = useState('');
    const [submittedFormData, setSubmittedFormData] = useState({} as OrderData);
    const [orderAsAlways, setOrderAsAlways] = useState(product === null);
    const successRef = useRef();
    const formRef = useRef();
    const location = useLocation();

    const selectedContainerLabel = watch('containerType');
    const selectedContainerAccessories = watch('containerAccessories', {});
    const selectedSupplyLabel = watch('supplyType');
    const selectedCustomerType = watch('customerType', product === null ? CustomerType.existing : CustomerType.new);
    const selectedBillingAddressType = watch('billingAddressType', product === null ? BillingAddressType.billing : BillingAddressType.delivery);
    const customerInformation = watch('customerInformation');
    const containerAmount = watch('containerAmount', 1);

    const selectedSupplyType = React.useMemo(
        () => supplyTypes.find(({ label }) => label === selectedSupplyLabel),
        [selectedSupplyLabel, supplyTypes]
    );

    const selectedContainerType = useMemo(() => {
        return product?.containerTypes.find((containerType) => containerType.label === selectedContainerLabel);
    }, [selectedContainerLabel]);

    const customerTypeOptions = useMemo(
        () => [
            {
                label: translate('orderForm.field.customerType.values.newCustomer', 'New customer'),
                value: CustomerType.new,
            },
            {
                label: translate('orderForm.field.customerType.values.existingCustomer', 'Existing customer'),
                value: CustomerType.existing,
            },
        ],
        [translate]
    );

    const billingAddressTypeOptions = useMemo(
        () => [
            {
                label: translate('orderForm.field.billingAddressType.values.deliveryAddress', 'Delivery address'),
                value: BillingAddressType.delivery,
            },
            {
                label: translate('orderForm.field.billingAddressType.values.billingAddress', 'Billing address'),
                value: BillingAddressType.billing,
            },
        ],
        [translate]
    );

    const isInquiry = useMemo(() => {
        if (explicitOrderMode === null || explicitOrderMode === true) {
            return selectedContainerType?.priceUponRequest ?? false;
        }
        return !explicitOrderMode;
    }, [explicitOrderMode, selectedContainerType]);

    const finalPrice = useMemo(() => {
        if (!selectedContainerType) return 0;
        let accessoryPrice = 0;

        if (selectedContainerAccessories) {
            accessoryPrice = Object.keys(selectedContainerAccessories).reduce((carry, accessoryTitle) => {
                const accessoryPrice =
                    selectedContainerType.accessories.find((accessory) => accessory.title == accessoryTitle)?.price ??
                    0;
                return carry + accessoryPrice * selectedContainerAccessories[accessoryTitle];
            }, 0);
        }
        return selectedContainerType.price * parseInt(containerAmount) + accessoryPrice;
    }, [selectedContainerType, selectedContainerAccessories, containerAmount]);

    useEffect(() => {
        if (location.hash === '#order-form') {
            scrollToRef(formRef);
        }
    }, [location]);

    const translateAndReplaceLink = (id, fallback, replace) => {
        return translate(id, fallback).replace(/(\[(.*)\]\(.*\))/, `<a href="${replace}" target="_blank">$2</a>`);
    };

    const toggleOrderAsUsual = useCallback(() => {
        if (product) {
            setFormValue('customerType', orderAsAlways ? CustomerType.new : CustomerType.existing, {
                shouldValidate: true,
            });
            setOrderAsAlways(!orderAsAlways);
        } else {
            handleReturn();
        }
    }, [setOrderAsAlways, orderAsAlways]);

    const onSubmit = useCallback(
        (data) => {
            if (isSubmitting) return;
            gtagReportOrder();
            submitOrder(data)
                .then(() => {
                    setSubmittedFormData(data);
                    setOrderSubmitted(true);
                    scrollToRef(successRef);
                    if (gtagEventTarget && window.gtag) {
                        window.gtag('event', 'conversion', { send_to: gtagEventTarget });
                    }
                })
                .catch((error) => {
                    setOrderFailed(true);
                    setOrderFeedback(error.message);
                });
        },
        [successRef, submitOrder, setOrderSubmitted, gtagEventTarget, setOrderFailed, setOrderFeedback]
    );

    const getPriceHint = (): string => {
        if (product?.alcTaxIncluded === true) {
            return translate(
                'orderForm.field.containerType.note.alcTaxIncluded.singular',
                'The indicated prices are net (incl. alcohol tax and excl. VAT)'
            );
        }

        return translate(
            'orderForm.field.containerType.note.singular',
            'The indicated prices are net (excl. VAT and alcohol tax)'
        );
    };

    return (
        <>
            {orderSubmitted ? (
                <div ref={successRef}>
                    <div className={classes.submittedLabel}>
                        {isShop && submittedFormData['isInquiry'] === 'true'
                            ? translate('message.inquirySent', 'Thank you for your order inquiry.')
                            : translate('message.orderSent', 'Thank you for your order.')}
                        <p>{submittedFormData['email']}</p>
                    </div>
                    <table className={classes.submittedFormData}>
                        <tbody>
                            {Object.keys(submittedFormData)
                                .filter(
                                    (fieldName) =>
                                        [
                                            'acceptPrivacyTerms',
                                            'acceptTermsAndConditions',
                                            'files',
                                            'email',
                                            'isInquiry',
                                            'alcTaxIncluded',
                                        ].indexOf(fieldName) === -1
                                )
                                .sort(
                                    (fieldName1, fieldName2) =>
                                        ConfirmationFieldOrder[fieldName1] - ConfirmationFieldOrder[fieldName2]
                                )
                                .map((fieldName) => (
                                    <tr key={fieldName}>
                                        <td>{translate(`orderForm.field.${fieldName}`, fieldName)}</td>
                                        <td>
                                            {typeof submittedFormData[fieldName] === 'object'
                                                ? Object.keys(submittedFormData[fieldName]).map((key) => (
                                                      <div key={key}>
                                                          {key}: {submittedFormData[fieldName][key]}
                                                      </div>
                                                  ))
                                                : translate(
                                                      `orderForm.field.${fieldName}.values.${submittedFormData[fieldName]}`,
                                                      submittedFormData[fieldName]
                                                  )}
                                        </td>
                                    </tr>
                                ))}
                            {submittedFormData['files'] && (
                                <tr>
                                    <td>{translate(`orderForm.field.files`, 'Files')}</td>
                                    <td>
                                        <ul>
                                            {Object.values(submittedFormData['files']).map((file: File) => (
                                                <li key={file.name}>{file.name}</li>
                                            ))}
                                        </ul>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            ) : (
                <form
                    ref={formRef}
                    id="order-form"
                    className={classes.form}
                    onSubmit={handleSubmit(onSubmit)}
                    encType="multipart/form-data"
                >
                    <h3>
                        {isInquiry
                            ? translate('orderForm.section.inquiry', 'Your inquiry')
                            : translate('orderForm.section.order', 'Your order')}
                    </h3>

                    {product && (
                        <>
                            <input type="hidden" name="productName" value={product.label} ref={register} />
                            {product.id && <input type="hidden" name="productId" value={product.id} ref={register} />}
                            <input type="hidden" name="isInquiry" value={isInquiry.toString()} ref={register} />
                            {isShop && !isInquiry && (
                                <input type="hidden" name="finalPrice" value={formatPrice(finalPrice)} ref={register} />
                            )}
                        </>
                    )}
                    {isShop && (
                        <>
                            <input
                                type="hidden"
                                name="alcTaxIncluded"
                                value={product?.alcTaxIncluded ? 'true' : 'false'}
                                ref={register}
                            />
                            <input
                                type="hidden"
                                name="orderAsAlways"
                                value={
                                    orderAsAlways
                                        ? translate('orderForm.field.orderAsAlways.yes', 'Order as always')
                                        : ''
                                }
                                ref={register}
                            />

                            {explicitOrderMode !== false && (
                                <OrderAsAlways enabled={orderAsAlways} onClick={toggleOrderAsUsual} />
                            )}
                        </>
                    )}

                    {isShop && !orderAsAlways && (
                        <>
                            <Select
                                name="containerType"
                                label={translate('orderForm.field.containerType', 'Container type')}
                                emptySelectionLabel={translate('orderForm.field.containerType.select', 'Please select')}
                                register={register}
                                required
                                options={product.containerTypes}
                                error={errors?.containerType}
                            />
                            {selectedContainerType && (
                                <>
                                    <Input
                                        type="number"
                                        name="containerAmount"
                                        label={translate('orderForm.field.containerAmount', 'Amount')}
                                        min={1}
                                        max={selectedContainerType.maximumPerOrder || null}
                                        defaultValue={1}
                                        readOnly={selectedContainerType.maximumPerOrder === 1}
                                        required
                                        register={register}
                                        error={errors?.containerType}
                                        validation={{
                                            min: 1,
                                            max: selectedContainerType.maximumPerOrder || null,
                                            validate: (value) => parseInt(value, 10) > 0,
                                        }}
                                    />

                                    {selectedContainerType.accessories.length > 0 && (
                                        <div className={classes.accessoryList}>
                                            {selectedContainerType.accessories.map((accessory) => (
                                                <Input
                                                    type="number"
                                                    key={accessory.identifier}
                                                    name={`containerAccessories[${accessory.title}]`}
                                                    label={
                                                        !isInquiry
                                                            ? `${accessory.label} ${formatPrice(accessory.price)}`
                                                            : `${accessory.label}`
                                                    }
                                                    defaultValue={0}
                                                    min={0}
                                                    register={register}
                                                    error={errors?.containerAccessories}
                                                    validation={{
                                                        min: 0,
                                                        validate: (value) => parseInt(value, 10) >= 0,
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    )}
                                    {!isInquiry && (
                                        <p>
                                            {translate('orderForm.field.finalPrice', 'Sum')}{' '}
                                            <strong>{formatPrice(finalPrice ?? 0)}</strong>
                                        </p>
                                    )}
                                </>
                            )}
                            {!isInquiry && <p>{getPriceHint()}</p>}

                            <br />

                            {locale !== 'en' && !product?.alcTaxIncluded && (
                                <Select
                                    name="supplyType"
                                    label={translate('orderForm.field.supplyType', 'Supply type')}
                                    emptySelectionLabel={translate(
                                        'orderForm.field.supplyType.select',
                                        'Please select'
                                    )}
                                    register={register}
                                    required
                                    options={supplyTypes}
                                    error={errors?.supplyType}
                                />
                            )}
                            {selectedSupplyType?.description && (
                                <div
                                    className={classes.supplyTypeDescription}
                                    dangerouslySetInnerHTML={{ __html: selectedSupplyType.description }}
                                />
                            )}
                        </>
                    )}

                    {allowUploads && (
                        <>
                            <Input
                                name="files"
                                label={translate('orderForm.field.files', 'Your files')}
                                type="file"
                                register={register}
                                required
                                multiple
                                error={errors?.files}
                                validation={{
                                    validate: (files) => {
                                        for (const file of files) {
                                            if (file.size > maxFileSize) return false;
                                        }
                                        return true;
                                    },
                                }}
                            />
                            {errors?.files?.type === 'validate' && (
                                <p>
                                    {translate('error.maxFileSize', 'One of the files is larger than {maxFileSize}', {
                                        maxFileSize: formatFileSize(maxFileSize, 0),
                                    })}
                                </p>
                            )}
                        </>
                    )}

                    <h3>{translate('orderForm.section.details', 'Your order details')}</h3>

                    {isShop && (
                        <RadioGroup
                            name="customerType"
                            defaultOption={product === null ? CustomerType.existing : CustomerType.new}
                            options={customerTypeOptions}
                            disabled={orderAsAlways}
                            register={register}
                        />
                    )}

                    {isShop && selectedCustomerType === CustomerType.existing && (
                        <>
                            <Input
                                name="customerInformation"
                                label={translate(
                                    'orderForm.field.customerInformation',
                                    'Customer number or company name'
                                )}
                                register={register}
                                required={orderAsAlways}
                                error={errors?.customerInformation}
                            />
                            <Input
                                name="zipcode"
                                label={translate('orderForm.field.zipcode', 'Zipcode')}
                                register={register}
                                required={orderAsAlways}
                                error={errors?.zipcode}
                            />
                        </>
                    )}

                    {!orderAsAlways && (
                        <Input
                            name="name"
                            label={translate('orderForm.field.name', 'Name')}
                            register={register}
                            required
                            error={errors?.name}
                        />
                    )}
                    <Input
                        name="email"
                        type="email"
                        label={translate('orderForm.field.email', 'Email')}
                        register={register}
                        required
                        error={errors?.email}
                        validation={{
                            pattern: {
                                value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                                message: translate(
                                    'orderForm.field.email.patternError',
                                    'Please enter a valid email address'
                                ),
                            },
                        }}
                    />
                    {isShop && selectedCustomerType === CustomerType.new && (
                        <>
                            <Input
                                name="phone"
                                label={translate('orderForm.field.phone', 'Phone')}
                                register={register}
                                required
                                error={errors?.phone}
                            />
                            <Input
                                name="vatNumber"
                                label={translate('orderForm.field.vatNumber', 'VAT No.')}
                                register={register}
                                disabled={!!customerInformation}
                                // TODO: This could be done with a regex explained here https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s21.html
                                // validation={{ pattern: { value: '', message: 'TODO' } }}
                                error={errors?.vatNumber}
                            />

                            <h3>{translate('orderForm.section.address', 'Delivery address')}</h3>
                        </>
                    )}

                    {isShop && !orderAsAlways && (
                        <Input
                            name="company"
                            label={translate('orderForm.field.company', 'Company')}
                            register={register}
                            required
                            error={errors?.company}
                        />
                    )}

                    {selectedCustomerType === CustomerType.new && (
                        <Address customerInformation={customerInformation} register={register} errors={errors} />
                    )}

                    <h3>{translate('orderForm.section.billingAddress', 'Billing address')}</h3>
                    <RadioGroup
                        name="billingAddressType"
                        defaultOption={product === null ? BillingAddressType.billing : BillingAddressType.delivery}
                        options={billingAddressTypeOptions}
                        register={register}
                        />
                    {selectedBillingAddressType === BillingAddressType.billing && (
                        <BillingAddress register={register} errors={errors} />
                    )}

                    {isShop && (
                    <>
                        <h3>{translate('orderForm.section.message', 'Message')}</h3>
                        <TextArea
                            name="message"
                            label={translate(
                                'orderForm.field.message',
                                'Gibt es sonst noch etwas, das Sie uns mitteilen möchten:'
                                )}
                                register={register}
                                error={errors?.message}
                                />
                    </>
                    )}

                    <CheckBox
                        name="acceptPrivacyTerms"
                        label={translateAndReplaceLink(
                            'orderForm.field.acceptPrivacyTerms',
                            'I accept the privacy terms',
                            privacyUri
                        )}
                        register={register}
                        required
                        error={errors?.acceptPrivacyTerms}
                    />
                    {isShop && (
                        <CheckBox
                            name="acceptTermsAndConditions"
                            label={translateAndReplaceLink(
                                'orderForm.field.acceptTermsAndConditions',
                                'I accept our terms and conditions',
                                termsAndConditionsUri
                            )}
                            register={register}
                            required
                            error={errors?.acceptTermsAndConditions}
                            />
                    )}

                    {orderFailed && (
                        <div className={classes.submissionError}>
                            <p>
                                {orderFeedback
                                    ? translate(orderFeedback, orderFeedback)
                                    : translate('error.orderNotSent', 'The form could not be submitted')}
                            </p>
                        </div>
                    )}

                    {isShop && (
                        <p className={classes.orderInfo}>
                            {translate(
                                'orderForm.payment',
                                'Payment by invoice only – 14 days net (or at the agreed terms)'
                            )}
                            <br />
                            {translate('orderForm.delivery', 'Receipt of delivery within 5-10 working days.')}
                            <br />
                            {translate(
                                'orderForm.deliveryExactDate',
                                'You will receive the exact delivery date in the order confirmation.'
                            )}
                            <br />
                            {translate(
                                'orderForm.saleComercialOnly',
                                'A sale is only to commercial customers.'
                                )}
                        </p>
                    )}

                    <div className={classes.submitButtonWrap}>
                        {Object.keys(errors).length > 0 && (
                            <p className={classes.errorInfo}>
                                {translate('orderForm.hasErrors', 'Please check your data')}
                            </p>
                        )}
                        <Button
                            onClick={handleSubmit(onSubmit)}
                            disabled={isSubmitting || (product && product.isNotAvailable)}
                            className={classes.orderButton}
                            label={
                                product && product.isNotAvailable
                                    ? translate('orderForm.action.isNotAvailable', 'not available')
                                    : isInquiry
                                    ? translate('orderForm.action.inquiry', 'inquire')
                                    : translate('orderForm.action.order', 'order now')
                            }
                        />
                    </div>
                </form>
            )}
        </>
    );
};

export default React.memo(ProductOrderForm);
